import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../api";
import { useI18n } from "../i18n/I18nContext";

import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";

// ── Helpers ──────────────────────────────────────────────────────────────────
const toF = (v) => { try { return parseFloat(v) || 0; } catch { return 0; } };

function isWithin30Days(dateStr) {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - 30);
  return d >= cutoff;
}

function fmtDateFull(str) {
  if (!str) return "—";
  return new Date(str).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function getPeriodLabel(periodMode, days, startDate, endDate) {
  if (periodMode === "custom") {
    if (startDate && endDate) {
      return `du ${fmtDateFull(startDate)} au ${fmtDateFull(endDate)}`;
    }
    if (startDate) {
      return `depuis le ${fmtDateFull(startDate)}`;
    }
    if (endDate) {
      return `jusqu'au ${fmtDateFull(endDate)}`;
    }
    return "toutes dates";
  }

  return `${days} derniers jours`;
}

function fmtDate(str) {
  if (!str) return "—";
  return new Date(str).toLocaleDateString("fr-FR", {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
  });
}

const COLORS = {
  green:  "#4ade80",
  red:    "#f87171",
  yellow: "#E6FF3E",
  blue:   "#60a5fa",
  orange: "#fb923c",
  purple: "#c084fc",
  teal:   "#2dd4bf",
  gray:   "#64748b",
};

const PIE_COLORS = [COLORS.red, COLORS.orange, COLORS.yellow, COLORS.purple];

// ── Composant Gauge ───────────────────────────────────────────────────────────
function Gauge({ label, value, total, color }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  const r = 54;
  const circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;

  return (
    <div style={S.gaugeWrap}>
      <svg width="130" height="130" viewBox="0 0 130 130">
        <circle cx="65" cy="65" r={r} fill="none" stroke="#1e3a50" strokeWidth="12" />
        <circle cx="65" cy="65" r={r} fill="none" stroke={color}
          strokeWidth="12" strokeDasharray={`${dash} ${circ}`}
          strokeLinecap="round"
          transform="rotate(-90 65 65)"
          style={{ transition: "stroke-dasharray 0.8s ease" }}
        />
        <text x="65" y="60" textAnchor="middle" fill="#fff" fontSize="22" fontWeight="800">
          {pct}%
        </text>
        <text x="65" y="80" textAnchor="middle" fill="#94a3b8" fontSize="10">
          {value}/{total}
        </text>
      </svg>
      <div style={S.gaugeLabel}>{label}</div>
    </div>
  );
}

// ── Composant KPI Card ────────────────────────────────────────────────────────
function KpiCard({ label, value, sub, color, icon }) {
  return (
    <div style={{ ...S.kpiCard, borderColor: color }}>
      <div style={S.kpiIcon}>{icon}</div>
      <div style={{ ...S.kpiValue, color }}>{value}</div>
      <div style={S.kpiLabel}>{label}</div>
      {sub && <div style={S.kpiSub}>{sub}</div>}
    </div>
  );
}

// ── Composant principal ───────────────────────────────────────────────────────
export default function DashboardQualiteGlobale() {
  const { t, ui } = useI18n();

  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");

  // Données brutes
  const [stacks,    setStacks]    = useState([]);
  const [planeites, setPlaneites] = useState([]);
  const [flexions,  setFlexions]  = useState([]);
  const [ilss,      setIlss]      = useState([]);
  const [mpar,      setMpar]      = useState([]);
  const [mpbois,    setMpbois]    = useState([]);
  const [mpcolle,   setMpcolle]   = useState([]);

  // Filtre période
  const [days, setDays] = useState(30);
  const [periodMode, setPeriodMode] = useState("preset");
const [startDate, setStartDate] = useState("");
const [endDate, setEndDate] = useState("");

  // ── Chargement parallèle ─────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [r1, r2, r3, r4, r5, r6, r7] = await Promise.all([
          apiFetch("/api/qualite/stack/list"),
          apiFetch("/api/qualite/stack_planeite/list"),
          apiFetch("/api/qualiteflexion/list"),
          apiFetch("/api/ilss/list"),
          apiFetch("/api/qualite/mpar/list"),
          apiFetch("/api/qualite/mpbois/list"),
          apiFetch("/api/qualite/mpcolle/list"),
        ]);
        const [d1, d2, d3, d4, d5, d6, d7] = await Promise.all([
          r1.json(), r2.json(), r3.json(), r4.json(), r5.json(), r6.json(), r7.json(),
        ]);
        setStacks(   Array.isArray(d1) ? d1 : d1.items || []);
        setPlaneites(Array.isArray(d2) ? d2 : d2.items || []);
        setFlexions( Array.isArray(d3) ? d3 : d3.items || []);
        setIlss(     Array.isArray(d4) ? d4 : d4.items || []);
        setMpar(     Array.isArray(d5) ? d5 : d5.items || []);
        setMpbois(   Array.isArray(d6) ? d6 : d6.items || []);
        setMpcolle(  Array.isArray(d7) ? d7 : d7.items || []);
      } catch (e) {
        setError("Erreur lors du chargement des données.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ── Filtre par période ───────────────────────────────────────────────────
function normalizeDateOnly(dateStr) {
  if (!dateStr) return "";
  return String(dateStr).split("T")[0];
}

function isWithinSelectedPeriod(dateStr) {
  const raw = normalizeDateOnly(dateStr);
  if (!raw) return false;

  if (periodMode === "custom") {
    if (!startDate && !endDate) return true;
    if (startDate && raw < startDate) return false;
    if (endDate && raw > endDate) return false;
    return true;
  }

  const today = new Date();
  const current = new Date(raw);
  const cutoff = new Date();
  cutoff.setDate(today.getDate() - days);

  current.setHours(0, 0, 0, 0);
  cutoff.setHours(0, 0, 0, 0);

  return current >= cutoff && current <= today;
}
  // ── Calculs ──────────────────────────────────────────────────────────────
  const stats = useMemo(() => {
   const fStacks    = stacks.filter(r    => isWithinSelectedPeriod(r.Date || r.Timestamp));
const fPlaneites = planeites.filter(r => isWithinSelectedPeriod(r.Date || r.Timestamp));
const fFlexions  = flexions.filter(r  => isWithinSelectedPeriod(r.Date || r.Timestamp));
const fIlss      = ilss.filter(r      => isWithinSelectedPeriod(r.Date || r.Timestamp));
const fMpar      = mpar.filter(r      => isWithinSelectedPeriod(r.Date || r.Timestamp));
const fMpbois    = mpbois.filter(r    => isWithinSelectedPeriod(r.Date || r.Timestamp));
const fMpcolle   = mpcolle.filter(r   => isWithinSelectedPeriod(r.Date || r.Timestamp));
    // Conformité Stack
    const stackConf     = fStacks.filter(r => r.Conformite === "CONFORME").length;
    const stackNonConf  = fStacks.filter(r => r.Conformite === "NON CONFORME").length;

    // Conformité Planéité
    const planConf      = fPlaneites.filter(r => r.Conformite === "CONFORME").length;
    const planNonConf   = fPlaneites.filter(r => r.Conformite === "NON CONFORME").length;

    // Conformité ILSS
    const ilssConf      = fIlss.filter(r => r.ConformiteILSS === "CONFORME").length;
    const ilssNonConf   = fIlss.filter(r => r.ConformiteILSS === "NON CONFORME").length;

    // Total mesures
    const totalMesures  = fStacks.length + fPlaneites.length + fFlexions.length + fIlss.length;
    const totalNonConf  = stackNonConf + planNonConf + ilssNonConf;
    const totalConf     = stackConf + planConf + ilssConf;

    // Opérateurs uniques
    const allOps = new Set([
      ...fStacks.map(r => r.Operateur || r.Operator),
      ...fPlaneites.map(r => r.Operateur),
      ...fFlexions.map(r => r.Operator),
      ...fIlss.map(r => r.Operator),
    ].filter(Boolean));

    // Top défauts
    const defauts = [
      { name: "Délamination",    value: fStacks.filter(r => r.Delamination).length },
      { name: "Planéité",        value: [...fStacks, ...fPlaneites].filter(r => r.Planeite).length },
      { name: "Défaut surface",  value: [...fStacks, ...fPlaneites].filter(r => r.DefautSurface).length },
      { name: "Autres défauts",  value: [...fStacks, ...fPlaneites].filter(r => r.AutresDefauts).length },
      { name: "Rés. collage",    value: fPlaneites.filter(r => r.ResistanceCollage).length },
    ].filter(d => d.value > 0).sort((a, b) => b.value - a.value);

    // Évolution conformité par jour (30 derniers jours)
    const dayMap = {};
    const now = new Date();
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      const key = d.toISOString().split("T")[0];
      dayMap[key] = { date: fmtDate(key), conforme: 0, nonConforme: 0 };
    }
    [...fStacks, ...fPlaneites].forEach(r => {
      const key = (r.Date || r.Timestamp || "").split("T")[0];
      if (!dayMap[key]) return;
      if (r.Conformite === "CONFORME") dayMap[key].conforme++;
      else if (r.Conformite === "NON CONFORME") dayMap[key].nonConforme++;
    });
    const evolutionData = Object.values(dayMap);

    // MP : Plis NOK par fournisseur
    const mpNokMap = {};
    [...fMpar, ...fMpbois, ...fMpcolle].forEach(r => {
      const f = r.Fournisseur || "Inconnu";
      if (!mpNokMap[f]) mpNokMap[f] = { fournisseur: f, bois: 0, resine: 0, colle: 0 };
    });
    fMpbois.forEach(r => {
      const f = r.Fournisseur || "Inconnu";
      if (!mpNokMap[f]) mpNokMap[f] = { fournisseur: f, bois: 0, resine: 0, colle: 0 };
      mpNokMap[f].bois += toF(r.PliesNOK);
    });
    fMpar.forEach(r => {
      const f = r.Fournisseur || "Inconnu";
      if (!mpNokMap[f]) mpNokMap[f] = { fournisseur: f, bois: 0, resine: 0, colle: 0 };
      mpNokMap[f].resine += toF(r.PliesNOK);
    });
    fMpcolle.forEach(r => {
      const f = r.Fournisseur || "Inconnu";
      if (!mpNokMap[f]) mpNokMap[f] = { fournisseur: f, bois: 0, resine: 0, colle: 0 };
      mpNokMap[f].colle += toF(r.PliesNOK);
    });
    const mpNokData = Object.values(mpNokMap)
      .filter(r => r.bois + r.resine + r.colle > 0)
      .sort((a, b) => (b.bois + b.resine + b.colle) - (a.bois + a.resine + a.colle));

    // Flexion & ILSS moyennes
    const bendingVals = fFlexions.map(r => toF(r.Bending_Modulus_MPa)).filter(v => v > 0);
    const bendingMoy  = bendingVals.length ? (bendingVals.reduce((a, b) => a + b, 0) / bendingVals.length).toFixed(0) : "—";
    const ilssBdb     = fIlss.filter(r => toF(r.ILS_BDB_MPa) > 0).map(r => toF(r.ILS_BDB_MPa));
    const ilss72h     = fIlss.filter(r => toF(r.ILS_72h_MPa) > 0).map(r => toF(r.ILS_72h_MPa));
    const ilssBdbMoy  = ilssBdb.length ? (ilssBdb.reduce((a, b) => a + b, 0) / ilssBdb.length).toFixed(2) : "—";
    const ilss72hMoy  = ilss72h.length ? (ilss72h.reduce((a, b) => a + b, 0) / ilss72h.length).toFixed(2) : "—";

    // Dernières non-conformités
    const nonConf = [
      ...fStacks.filter(r => r.Conformite === "NON CONFORME").map(r => ({
        date: r.Date || r.Timestamp?.split("T")[0], stack: r.IDStack,
        type: "Stack", op: r.Operateur,
        defauts: [r.Delamination && "Délamination", r.Planeite && "Planéité",
                  r.DefautSurface && "Défaut surface", r.AutresDefauts && "Autres"].filter(Boolean).join(", "),
      })),
      ...fPlaneites.filter(r => r.Conformite === "NON CONFORME").map(r => ({
        date: r.Date || r.Timestamp?.split("T")[0], stack: r.IDStack,
        type: "Planéité", op: r.Operateur,
        defauts: [r.Planeite && "Planéité", r.ResistanceCollage && "Rés. collage",
                  r.DefautSurface && "Défaut surface", r.AutresDefauts && "Autres"].filter(Boolean).join(", "),
      })),
      ...fIlss.filter(r => r.Conformite_ILSS === "NON CONFORME").map(r => ({
        date: r.Date || r.Timestamp?.split("T")[0], stack: r.IDStack,
        type: "ILSS", op: r.Operator, defauts: `Typ: ${r.Typologie || "—"}`,
      })),
    ].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 15);

    return {
      fStacks, fPlaneites, fFlexions, fIlss,
      stackConf, stackNonConf,
      planConf, planNonConf,
      ilssConf, ilssNonConf,
      totalMesures, totalNonConf, totalConf,
      nbOps: allOps.size,
      defauts, evolutionData, mpNokData,
      bendingMoy, ilssBdbMoy, ilss72hMoy,
      nonConf,
    };
  }, [stacks, planeites, flexions, ilss, mpar, mpbois, mpcolle, periodMode, days, startDate, endDate,]);

  // ── Rendu ────────────────────────────────────────────────────────────────
  if (loading) return (
    <div style={{ ...S.page, justifyContent: "center", alignItems: "center" }}>
      <div style={S.loadingBox}>
        <div style={S.spinner} />
        <div style={{ color: "#94a3b8", marginTop: 16 }}>Chargement du dashboard…</div>
      </div>
    </div>
  );

  if (error) return (
    <div style={{ ...S.page, justifyContent: "center", alignItems: "center" }}>
      <div style={{ color: "#f87171", fontSize: 16 }}>{error}</div>
    </div>
  );

  const totalStack    = stats.stackConf + stats.stackNonConf;
  const totalPlan     = stats.planConf + stats.planNonConf;
  const totalIlss     = stats.ilssConf + stats.ilssNonConf;
  const globalConfPct = (stats.totalConf + stats.totalNonConf) > 0
    ? Math.round(stats.totalConf / (stats.totalConf + stats.totalNonConf) * 100)
    : 0;
  const periodLabel = getPeriodLabel(periodMode, days, startDate, endDate);
  const displayedDays =
  periodMode === "custom" && startDate && endDate
    ? Math.max(
        1,
        Math.ceil(
          (new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24)
        ) + 1
      )
    : days;

  return (
    <div style={S.page}>
      <div style={S.wrap}>

        {/* ── Header ── */}
        <div style={S.header}>
          <div>
            <div style={S.title}>Dashboard Qualité</div>
            <div style={S.subtitle}>Vue globale de la conformité production</div>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            {/* Filtre période */}
           <div style={S.periodControls}>
  <div style={S.periodBar}>
    {[1,7, 30, 90].map((d) => (
      <button
        key={d}
        style={{
          ...S.periodBtn,
          ...(periodMode === "preset" && days === d ? S.periodBtnActive : {}),
        }}
        onClick={() => {
          setPeriodMode("preset");
          setDays(d);
        }}
      >
        {d}j
      </button>
    ))}

    <button
      style={{
        ...S.periodBtn,
        ...(periodMode === "custom" ? S.periodBtnActive : {}),
      }}
      onClick={() => setPeriodMode("custom")}
    >
      Personnalisé
    </button>
  </div>

  {periodMode === "custom" && (
    <div style={S.dateRangeBox}>
      <div style={S.dateField}>
        <label style={S.dateLabel}>Du</label>
        <input
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          style={S.dateInput}
        />
      </div>

      <div style={S.dateField}>
        <label style={S.dateLabel}>Au</label>
        <input
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          style={S.dateInput}
        />
      </div>
    </div>
  )}
</div>
            <button style={S.backBtn} onClick={() => navigate(-1)}>← Retour</button>
          </div>
        </div>

                {/* ── KPIs ── */}
        <div style={S.kpiRow}>
          <KpiCard icon="📊" label="Conformité globale"
            value={`${globalConfPct}%`}
            sub={`${stats.totalConf} conformes`}
            color={globalConfPct >= 80 ? COLORS.green : globalConfPct >= 60 ? COLORS.yellow : COLORS.red} />
          <KpiCard
  icon="🔬"
  label="Total mesures"
  value={stats.totalMesures}
  sub={periodLabel}
  color={COLORS.blue}
/>
          <KpiCard icon="⚠️" label="Non-conformités"
            value={stats.totalNonConf}
            sub={`${stats.fStacks.length} stacks analysés`}
            color={stats.totalNonConf > 0 ? COLORS.red : COLORS.green} />
          <KpiCard
  icon="👷"
  label="Opérateurs actifs"
  value={stats.nbOps}
  sub={periodLabel}
  color={COLORS.teal}
/>
          <KpiCard icon="🪵" label="Flexion moy."
            value={stats.bendingMoy === "—" ? "—" : `${stats.bendingMoy} MPa`}
            sub={`${stats.fFlexions.length} tests`}
            color={COLORS.purple} />
          <KpiCard icon="⚡" label="ILSS BDB moy."
            value={stats.ilssBdbMoy === "—" ? "—" : `${stats.ilssBdbMoy} MPa`}
            sub={`ILSS 72H : ${stats.ilss72hMoy} MPa`}
            color={COLORS.orange} />
        </div>

        {/* ── Ligne 2 : Gauges + Évolution ── */}
        <div style={S.row2}>

          {/* Gauges conformité */}
          <div style={S.card}>
            <div style={S.cardTitle}>Conformité par module</div>
            <div style={S.gaugesRow}>
              <Gauge label="Stack"    value={stats.stackConf} total={totalStack}  color={COLORS.green} />
              <Gauge label="Planéité" value={stats.planConf}  total={totalPlan}   color={COLORS.blue} />
              <Gauge label="ILSS"     value={stats.ilssConf}  total={totalIlss}   color={COLORS.purple} />
            </div>
          </div>

          {/* Évolution conformité */}
          <div style={{ ...S.card, flex: 2 }}>
           <div style={S.cardTitle}>Évolution conformité — {periodLabel}</div>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={stats.evolutionData} margin={{ top: 8, right: 20, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e3a50" />
                <XAxis dataKey="date" tick={{ fill: "#64748b", fontSize: 11 }}
                  interval={displayedDays <= 7 ? 0 : displayedDays <= 30 ? 4 : 9} />
                <YAxis tick={{ fill: "#64748b", fontSize: 11 }} />
                <Tooltip
                  contentStyle={{ background: "#071e2f", border: "1px solid #1e3a50", borderRadius: 8 }}
                  labelStyle={{ color: "#E6FF3E" }}
                  itemStyle={{ color: "#cbd5e1" }} />
                <Legend wrapperStyle={{ fontSize: 12, color: "#94a3b8" }} />
                <Line type="monotone" dataKey="conforme"    name="Conforme"
                  stroke={COLORS.green}  strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="nonConforme" name="Non conforme"
                  stroke={COLORS.red}    strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* ── Ligne 3 : Top défauts + MP NOK ── */}
        <div style={S.row2}>

          {/* Camembert défauts */}
          <div style={S.card}>
            <div style={S.cardTitle}>Top défauts détectés</div>
            {stats.defauts.length === 0 ? (
              <div style={S.emptyState}>✅ Aucun défaut sur la période</div>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={stats.defauts} dataKey="value" nameKey="name"
                    cx="50%" cy="50%" outerRadius={80} label={({ name, value }) => `${name}: ${value}`}
                    labelLine={{ stroke: "#1e3a50" }}>
                    {stats.defauts.map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ background: "#071e2f", border: "1px solid #1e3a50", borderRadius: 8 }}
                    itemStyle={{ color: "#cbd5e1" }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Barres MP NOK */}
          <div style={{ ...S.card, flex: 2 }}>
            <div style={S.cardTitle}>Plis NOK par fournisseur — Matières premières</div>
            {stats.mpNokData.length === 0 ? (
              <div style={S.emptyState}>✅ Aucun pli NOK sur la période</div>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={stats.mpNokData} margin={{ top: 8, right: 20, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e3a50" />
                  <XAxis dataKey="fournisseur" tick={{ fill: "#64748b", fontSize: 11 }} />
                  <YAxis tick={{ fill: "#64748b", fontSize: 11 }} />
                  <Tooltip
                    contentStyle={{ background: "#071e2f", border: "1px solid #1e3a50", borderRadius: 8 }}
                    labelStyle={{ color: "#E6FF3E" }}
                    itemStyle={{ color: "#cbd5e1" }} />
                  <Legend wrapperStyle={{ fontSize: 12, color: "#94a3b8" }} />
                  <Bar dataKey="bois"   name="Bois"   fill={COLORS.orange} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="resine" name="Résine" fill={COLORS.blue}   radius={[4, 4, 0, 0]} />
                  <Bar dataKey="colle"  name="Colle"  fill={COLORS.purple} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* ── Ligne 4 : Tableau non-conformités ── */}
        <div style={S.card}>
          <div style={S.cardTitle}>
            Dernières non-conformités
            <span style={S.cardBadge}>{stats.nonConf.length}</span>
          </div>
          {stats.nonConf.length === 0 ? (
            <div style={S.emptyState}>✅ Aucune non-conformité sur la période</div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={S.table}>
                <thead>
                  <tr>
                    {["Date", "ID Stack", "Module", "Défauts", "Opérateur"].map(h => (
                      <th key={h} style={S.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {stats.nonConf.map((r, i) => (
                    <tr key={i} style={i % 2 === 0 ? S.trEven : S.trOdd}>
                      <td style={S.td}>{r.date || "—"}</td>
                      <td style={{ ...S.td, color: "#E6FF3E", fontWeight: 700, fontFamily: "monospace" }}>
                        {r.stack || "—"}
                      </td>
                      <td style={S.td}>
                        <span style={{
                          ...S.badge,
                          background: r.type === "Stack" ? "rgba(96,165,250,0.15)"
                            : r.type === "Planéité" ? "rgba(192,132,252,0.15)"
                            : "rgba(251,146,60,0.15)",
                          color: r.type === "Stack" ? COLORS.blue
                            : r.type === "Planéité" ? COLORS.purple
                            : COLORS.orange,
                          borderColor: r.type === "Stack" ? "rgba(96,165,250,0.3)"
                            : r.type === "Planéité" ? "rgba(192,132,252,0.3)"
                            : "rgba(251,146,60,0.3)",
                        }}>
                          {r.type}
                        </span>
                      </td>
                      <td style={{ ...S.td, color: "#f87171", fontSize: 12 }}>{r.defauts || "—"}</td>
                      <td style={{ ...S.td, color: "#94a3b8" }}>{r.op || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const S = {
  page:         { minHeight: "100vh", background: "linear-gradient(160deg,#041e2e 0%,#063549 60%,#052a3e 100%)", color: "#e2e8f0", fontFamily: "'Inter',sans-serif", padding: "24px 28px" },
  wrap:         { maxWidth: 1600, margin: "0 auto", display: "flex", flexDirection: "column", gap: 20 },
  header:       { display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 },
  title:        { fontSize: 28, fontWeight: 900, background: "linear-gradient(90deg,#fff 0%,#E6FF3E 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" },
  subtitle:     { fontSize: 13, color: "#94a3b8", marginTop: 4 },
  backBtn:      { padding: "9px 18px", borderRadius: 8, border: "1px solid #1e3a50", background: "rgba(255,255,255,0.04)", color: "#cbd5e1", cursor: "pointer", fontWeight: 700, fontSize: 13 },
  periodBar:    { display: "flex", gap: 4, background: "rgba(255,255,255,0.04)", border: "1px solid #1e3a50", borderRadius: 8, padding: 4 },
  periodBtn:    { padding: "6px 14px", borderRadius: 6, border: "none", background: "transparent", color: "#94a3b8", cursor: "pointer", fontWeight: 700, fontSize: 13 },
  periodBtnActive: { background: "#E6FF3E", color: "#000" },
  kpiRow:       { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px,1fr))", gap: 14 },
  kpiCard:      { background: "rgba(255,255,255,0.03)", border: "1px solid #1e3a50", borderRadius: 14, padding: "18px 20px", display: "flex", flexDirection: "column", alignItems: "center", gap: 6, borderTopWidth: 3, backdropFilter: "blur(8px)" },
  kpiIcon:      { fontSize: 24 },
    periodControls: {
  display: "flex",
  flexDirection: "column",
  gap: 8,
  alignItems: "flex-end",
},

dateRangeBox: {
  display: "flex",
  gap: 8,
  flexWrap: "wrap",
  justifyContent: "flex-end",
},

dateField: {
  display: "flex",
  flexDirection: "column",
  gap: 4,
},

dateLabel: {
  fontSize: 11,
  color: "#94a3b8",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
},

dateInput: {
  padding: "8px 12px",
  borderRadius: 8,
  border: "1px solid #1e3a50",
  background: "rgba(255,255,255,0.04)",
  color: "#cbd5e1",
  fontWeight: 600,
  fontSize: 13,
  outline: "none",
},
  kpiValue:     { fontSize: 32, fontWeight: 900 },
  kpiLabel:     { fontSize: 12, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", textAlign: "center" },
  kpiSub:       { fontSize: 11, color: "#64748b" },
  row2:         { display: "grid", gridTemplateColumns: "1fr 2fr", gap: 16 },
  card:         { background: "rgba(255,255,255,0.03)", border: "1px solid #1e3a50", borderRadius: 14, padding: "20px 22px", backdropFilter: "blur(8px)" },
  cardTitle:    { fontSize: 13, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 16, display: "flex", alignItems: "center", gap: 8 },
  cardBadge:    { background: "#ef4444", color: "#fff", borderRadius: 999, padding: "2px 8px", fontSize: 11, fontWeight: 800 },
  gaugesRow:    { display: "flex", justifyContent: "space-around", alignItems: "center", flexWrap: "wrap", gap: 8 },
  gaugeWrap:    { display: "flex", flexDirection: "column", alignItems: "center", gap: 6 },
  gaugeLabel:   { fontSize: 12, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.07em" },
  emptyState:   { textAlign: "center", padding: "40px 20px", color: "#4ade80", fontSize: 14, fontWeight: 600 },
  table:        { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th:           { padding: "11px 14px", background: "#071e2f", color: "#94a3b8", fontWeight: 700, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.07em", textAlign: "left", borderBottom: "1px solid #1e3a50", whiteSpace: "nowrap" },
  td:           { padding: "11px 14px", borderBottom: "1px solid #0f2a3d", color: "#cbd5e1", whiteSpace: "nowrap" },
  trEven:       { background: "transparent" },
  trOdd:        { background: "rgba(255,255,255,0.015)" },
  badge:        { display: "inline-flex", alignItems: "center", padding: "3px 10px", borderRadius: 20, fontWeight: 700, fontSize: 11, border: "1px solid" },
  loadingBox:   { display: "flex", flexDirection: "column", alignItems: "center", gap: 12 },
  spinner:      { width: 40, height: 40, border: "3px solid #1e3a50", borderTop: "3px solid #E6FF3E", borderRadius: "50%", animation: "spin 1s linear infinite" },
};